import { FC, SyntheticEvent, useCallback, useMemo } from 'react';
import cls from 'classnames';
import PlayFill from 'icon/play_fill.svg';
import Heart from 'icon/heart.svg';
import _clamp from 'lodash/clamp';
import { EArticleClass, TArticleDetail } from 'shared/models/article';
import { EClickPosition } from 'shared/constant';
import { useCTADirectly } from '~/utils/cta';
import { activeToArticle } from '~/utils/active/active-to-article';
import { useNumFormatter } from '@lemon8/web-app-shared/hook';
import { EPageModule } from 'shared/constant/page-module';
import { useViewTracking } from '~/utils/view-tracking';
import { sendCTAShowEvent, sendRelatedPostShowEvent } from '~/utils/event';
import { useAtomValue } from 'jotai';
import { $UseVideoPlayBtnCTA } from '~/utils/libra/cta-opt';

import './style.scss';

interface ArticleRecommendCardProps {
  pageModule: EPageModule;
  clickPosition: EClickPosition.RelatedPost | EClickPosition.AuthorPost;
  article: TArticleDetail;
  rank: number;
}

export const ArticleRecommendCard: FC<ArticleRecommendCardProps> = (props) => {
  const formatNum = useNumFormatter();
  const useVideoPlayBtnCTA = useAtomValue($UseVideoPlayBtnCTA);
  const { article, clickPosition, pageModule, rank } = props;

  const cardObservedRef = useViewTracking({
    onView: () => {
      sendCTAShowEvent({
        pageModule,
        currentPosition: clickPosition,
      });
      sendRelatedPostShowEvent({
        pageModule,
        clickPosition,
        groupId: article.groupId,
        mediaId: article.author.userId,
        articleClass: article.articleClass,
        rank,
      });
    },
  });

  const handleClick = useCTADirectly({
    clickPosition,
    clickId: article.groupId,
    onClick: useCallback(() => {
      activeToArticle(article, clickPosition, article.groupId);
    }, [article, clickPosition]),
  });

  const aspectRatio = useMemo(() => {
    switch (article.articleClass) {
      case EArticleClass.GALLERY:
        return _clamp(article.imageList[0].width / article.imageList[0].height, 3 / 4, 4 / 3);
      case EArticleClass.ARTICLE:
      case EArticleClass.VIDEO:
      default:
        return _clamp(article.largeImage.width / article.largeImage.height, 3 / 4, 4 / 3);
    }
  }, [article]);

  const coverUrl = useMemo(() => {
    switch (article.articleClass) {
      case EArticleClass.GALLERY:
        return article.imageList[0].url;
      case EArticleClass.ARTICLE:
      case EArticleClass.VIDEO:
      default:
        return article.largeImage.url;
    }
  }, [article]);

  return (
    <div className="article-recommend-card" onClick={handleClick} ref={cardObservedRef}>
      <div
        className={cls('article-cover', {
          video: article.articleClass === EArticleClass.VIDEO,
        })}
        style={{ paddingBottom: `${(1 / aspectRatio) * 100}%` }}
      >
        <img loading="lazy" src={coverUrl} onContextMenu={(e: SyntheticEvent) => e.preventDefault()} />
        {useVideoPlayBtnCTA ? (
          <PlayFill className="play-icon-cta" />
        ) : (
          article.articleClass === EArticleClass.VIDEO && <PlayFill className="play-icon" />
        )}
      </div>
      <div className="article-content">
        <div className="article-title">{article.title}</div>
        <div className="article-desc">
          <img
            loading="lazy"
            className="author-avatar"
            src={article.author.avatar}
            onContextMenu={(e: SyntheticEvent) => e.preventDefault()}
          />
          <span className="author-name">{article.author.nickName}</span>
          <div className="digg">
            <Heart />
            {Boolean(article.diggCount) && <span>{formatNum(article.diggCount)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};
